import React, { useEffect, useMemo, useState } from 'react';
import type { FastingSession, Language } from '../types';
import { getTranslation } from '../utils/translations';
import XIcon from './icons/XIcon';
import PencilIcon from './icons/PencilIcon';
import TrashIcon from './icons/TrashIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import ChevronUpIcon from './icons/ChevronUpIcon';
import ConfirmationModal from './ConfirmationModal';
import FastingDateTimeField from './fasting/FastingDateTimeField';
import { formatClockTime, isDateTimeLocalInRange, parseDateTimeLocalValue, toDateTimeLocalValue } from '../utils/intermittentFasting';
import PlusIcon from './icons/PlusIcon';
import {
    FASTING_PERIOD_COLORS,
    buildFastingChartDays,
    createCompletedFastingSession,
    formatFastingDuration,
    formatFastingWeekdayLabel,
    getFastingChartMaxMinutes,
    groupFastingSessionsByDate,
    subscribeCompletedFastingSessions,
    getElapsedMinutes,
    deleteFastingSession,
    updateFastingSession,
    correctLongFastingSessions,
    toLocalDateKey,
} from '../utils/fastingSessions';

interface FastingStatisticsModalProps {
    isOpen: boolean;
    onClose: () => void;
    userId: string;
    language?: Language;
}

const CHART_DAYS = 14;
const CHART_HEIGHT_PX = 120;
const CHART_LABEL_HEIGHT_PX = 20;

const FastingStatisticsModal: React.FC<FastingStatisticsModalProps> = ({
    isOpen,
    onClose,
    userId,
    language = 'en',
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const [sessions, setSessions] = useState<FastingSession[]>([]);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [expandedDays, setExpandedDays] = useState<Record<string, boolean>>({});
    const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState({ startedAt: '', endedAt: '' });
    const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
    const [isAddingEntry, setIsAddingEntry] = useState(false);
    const [addForm, setAddForm] = useState({ startedAt: '', endedAt: '' });

    useEffect(() => {
        if (!isOpen) {
            setSelectedDate(null);
            setExpandedDays({});
            setEditingSessionId(null);
            setIsAddingEntry(false);
            setAddForm({ startedAt: '', endedAt: '' });
        }
    }, [isOpen]);

    const buildDefaultAddForm = () => {
        const ended = new Date();
        ended.setSeconds(0, 0);
        const started = new Date(ended.getTime() - 16 * 60 * 60 * 1000);
        return {
            startedAt: toDateTimeLocalValue(started),
            endedAt: toDateTimeLocalValue(ended),
        };
    };

    const openAddEntryForm = () => {
        setEditingSessionId(null);
        setIsAddingEntry(true);
        setAddForm(buildDefaultAddForm());
    };

    useEffect(() => {
        if (!isOpen || !userId) return;
        return subscribeCompletedFastingSessions(userId, setSessions);
    }, [isOpen, userId]);

    useEffect(() => {
        if (!isOpen || !userId || sessions.length === 0) return;
        correctLongFastingSessions(userId, sessions).catch(console.error);
    }, [isOpen, userId, sessions]);

    const toggleDay = (date: string) => {
        setExpandedDays((prev) => ({ ...prev, [date]: !prev[date] }));
    };

    const handleEditClick = (session: FastingSession) => {
        setIsAddingEntry(false);
        setEditingSessionId(session.id);
        setEditForm({
            startedAt: toDateTimeLocalValue(new Date(session.startedAt)),
            endedAt: session.endedAt
                ? toDateTimeLocalValue(new Date(session.endedAt))
                : toDateTimeLocalValue(new Date()),
        });
    };

    const handleSaveEdit = async () => {
        if (!editingSessionId || !userId) return;
        try {
            const startedAt = new Date(editForm.startedAt).toISOString();
            const endedAt = new Date(editForm.endedAt).toISOString();
            await updateFastingSession(userId, editingSessionId, { startedAt, endedAt });
            setEditingSessionId(null);
        } catch (error) {
            console.error('Failed to update session', error);
        }
    };

    const nowLocalValue = toDateTimeLocalValue(new Date());

    const isValidSessionRange = (startedAt: string, endedAt: string): boolean => {
        const start = parseDateTimeLocalValue(startedAt);
        const end = parseDateTimeLocalValue(endedAt);
        if (!start || !end || end.getTime() <= start.getTime()) return false;
        return isDateTimeLocalInRange(startedAt, undefined, nowLocalValue)
            && isDateTimeLocalInRange(endedAt, undefined, nowLocalValue);
    };

    const handleSaveAddEntry = async () => {
        if (!userId || !isValidSessionRange(addForm.startedAt, addForm.endedAt)) return;
        try {
            await createCompletedFastingSession(
                userId,
                new Date(addForm.startedAt).toISOString(),
                new Date(addForm.endedAt).toISOString(),
            );
            const addedDate = toLocalDateKey(new Date(addForm.startedAt).toISOString());
            setIsAddingEntry(false);
            setSelectedDate(addedDate);
            setExpandedDays((prev) => ({ ...prev, [addedDate]: true }));
        } catch (error) {
            console.error('Failed to add fasting entry', error);
        }
    };

    const confirmDelete = async () => {
        if (!userId || !sessionToDelete) return;
        try {
            await deleteFastingSession(userId, sessionToDelete);
        } catch (error) {
            console.error('Failed to delete session', error);
        } finally {
            setSessionToDelete(null);
        }
    };

    const handleDelete = (sessionId: string) => {
        setSessionToDelete(sessionId);
    };

    const daySummaries = useMemo(() => groupFastingSessionsByDate(sessions), [sessions]);

    const chartData = useMemo(
        () => buildFastingChartDays(sessions, CHART_DAYS),
        [sessions],
    );

    const chartMaxMinutes = useMemo(
        () => getFastingChartMaxMinutes(chartData),
        [chartData],
    );

    const chartPlotHeight = CHART_HEIGHT_PX - CHART_LABEL_HEIGHT_PX;

    const formatDayTotal = (totalMinutes: number): string => {
        const { hours, minutes } = formatFastingDuration(totalMinutes);
        if (hours > 0 && minutes > 0) {
            return `${hours}${t('fastingHoursShort')} ${minutes}${t('fastingMinutesShort')}`;
        }
        if (hours > 0) return `${hours}${t('fastingHoursShort')}`;
        return `${minutes}${t('fastingMinutesShort')}`;
    };

    const formatDateLabel = (dateKey: string): string => {
        const [y, m, d] = dateKey.split('-').map(Number);
        const date = new Date(y, m - 1, d);
        return date.toLocaleDateString(language, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    };

    const sessionBarHeightPx = (durationMinutes: number): number => {
        if (durationMinutes <= 0) return 0;
        return Math.max(4, Math.round((durationMinutes / chartMaxMinutes) * chartPlotHeight));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[180] flex items-center justify-center p-4" onClick={onClose}>
            <div
                className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col border border-slate-200 dark:border-slate-700"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 border-b border-slate-100 dark:border-slate-800">
                    <h2 className="text-lg font-black text-slate-800 dark:text-slate-100">{t('fastingStatistics')}</h2>
                    <button type="button" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600">
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                <div className="overflow-y-auto flex-1 p-5 space-y-5">
                    <div className="rounded-xl border border-slate-100 dark:border-slate-800 p-4 bg-slate-50/50 dark:bg-slate-800/30">
                        <div className="flex items-center justify-between gap-2 mb-4">
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                {t('fastingWeeklyChart')}
                            </p>
                            <div className="flex items-center gap-2">
                                {selectedDate && (
                                    <button
                                        type="button"
                                        onClick={() => setSelectedDate(null)}
                                        className="text-[11px] font-bold text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300 transition-colors"
                                    >
                                        {t('fastingClearSelection')}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={openAddEntryForm}
                                    className="inline-flex items-center gap-1 text-[11px] font-bold text-teal-700 dark:text-teal-300 hover:text-teal-900 dark:hover:text-teal-200 transition-colors"
                                >
                                    <PlusIcon className="w-3.5 h-3.5" />
                                    {t('fastingAddEntry')}
                                </button>
                            </div>
                        </div>
                        <div
                            className="flex items-end justify-between gap-0.5"
                            style={{ height: CHART_HEIGHT_PX }}
                        >
                            {chartData.map((day) => (
                                <div
                                    key={day.date}
                                    className={`flex-1 min-w-0 flex flex-col items-center justify-end h-full cursor-pointer transition-opacity ${selectedDate && selectedDate !== day.date ? 'opacity-30' : 'opacity-100'}`}
                                    onClick={() => setSelectedDate(prev => prev === day.date ? null : day.date)}
                                >
                                    <div
                                        className="flex flex-col-reverse items-center justify-start w-full"
                                        style={{ height: chartPlotHeight }}
                                    >
                                        {day.sessions.length === 0 ? (
                                            <div
                                                className="w-1.5 rounded-t-sm bg-slate-200 dark:bg-slate-700"
                                                style={{ height: 3 }}
                                                aria-hidden
                                            />
                                        ) : (
                                            day.sessions.map((session) => (
                                                <div
                                                    key={session.id}
                                                    className="w-[12px] transition-all first:rounded-b-sm last:rounded-t-sm"
                                                    style={{
                                                        height: sessionBarHeightPx(session.durationMinutes),
                                                        backgroundColor: FASTING_PERIOD_COLORS[session.colorIndex],
                                                    }}
                                                    title={formatDayTotal(session.durationMinutes)}
                                                />
                                            ))
                                        )}
                                    </div>
                                    <span
                                        className="text-[11px] font-bold text-slate-500 dark:text-slate-400 leading-none mt-1.5 text-center w-full"
                                        title={formatDateLabel(day.date)}
                                    >
                                        {formatFastingWeekdayLabel(day.date, language)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {isAddingEntry && (
                        <div className="rounded-xl border border-teal-200 dark:border-teal-900/40 p-4 bg-teal-50/40 dark:bg-teal-900/10 space-y-3">
                            <div className="flex items-center justify-between gap-2">
                                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                                    {t('fastingAddEntryTitle')}
                                </h3>
                                <button
                                    type="button"
                                    onClick={() => setIsAddingEntry(false)}
                                    className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                    aria-label={t('close')}
                                >
                                    <XIcon className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="flex flex-col gap-3">
                                <div>
                                    <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 block">{t('fastingEditStartLabel')}</label>
                                    <FastingDateTimeField
                                        value={addForm.startedAt}
                                        onChange={(startedAt) => setAddForm((prev) => ({ ...prev, startedAt }))}
                                        language={language}
                                        max={nowLocalValue}
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 block">{t('fastingEditEndLabel')}</label>
                                    <FastingDateTimeField
                                        value={addForm.endedAt}
                                        onChange={(endedAt) => setAddForm((prev) => ({ ...prev, endedAt }))}
                                        language={language}
                                        max={nowLocalValue}
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2 justify-end pt-1">
                                <button
                                    type="button"
                                    onClick={() => setIsAddingEntry(false)}
                                    className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg border border-slate-200 dark:border-slate-600 transition-colors"
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveAddEntry}
                                    disabled={!isValidSessionRange(addForm.startedAt, addForm.endedAt)}
                                    className="px-4 py-2 text-xs font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-lg shadow-sm transition-colors disabled:opacity-50"
                                >
                                    {t('save')}
                                </button>
                            </div>
                        </div>
                    )}

                    {daySummaries.length === 0 && !isAddingEntry ? (
                        <p className="text-sm text-slate-500 text-center py-4">{t('fastingNoHistory')}</p>
                    ) : (
                        daySummaries
                            .filter((day) => !selectedDate || day.date === selectedDate)
                            .map((day) => (
                            <div key={day.date} className="rounded-xl border border-slate-100 dark:border-slate-800 p-4 bg-slate-50/50 dark:bg-slate-800/30">
                                <div 
                                    className="flex items-center justify-between gap-2 cursor-pointer"
                                    onClick={() => toggleDay(day.date)}
                                >
                                    <div className="flex items-center gap-2 text-slate-800 dark:text-slate-100">
                                        {expandedDays[day.date] ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                                        <span className="text-sm font-bold">
                                            {formatDateLabel(day.date)}
                                        </span>
                                    </div>
                                    <span className="text-sm font-black text-violet-700 dark:text-violet-300">
                                        {formatDayTotal(day.totalMinutes)}
                                    </span>
                                </div>
                                
                                {expandedDays[day.date] && (
                                    <div className="mt-4 flex flex-col gap-3">
                                        {day.sessions.map((session, index) => {
                                            const startStr = formatClockTime(new Date(session.startedAt).getTime(), language);
                                            const endStr = session.status === 'completed' && session.endedAt
                                                ? formatClockTime(new Date(session.endedAt).getTime(), language)
                                                : (t('fastingActive') || 'Active');
                                            
                                            const duration = session.status === 'active' ? getElapsedMinutes(session) : (session.durationMinutes ?? 0);
                                            
                                            if (editingSessionId === session.id) {
                                                return (
                                                    <div key={session.id} className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-violet-200 dark:border-violet-800/50 space-y-3 shadow-sm">
                                                        <div className="flex flex-col gap-3">
                                                            <div>
                                                                <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 block">{t('fastingEditStartLabel')}</label>
                                                                <FastingDateTimeField
                                                                    value={editForm.startedAt}
                                                                    onChange={(startedAt) => setEditForm((prev) => ({ ...prev, startedAt }))}
                                                                    language={language}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 block">{t('fastingEditEndLabel')}</label>
                                                                <FastingDateTimeField
                                                                    value={editForm.endedAt}
                                                                    onChange={(endedAt) => setEditForm((prev) => ({ ...prev, endedAt }))}
                                                                    language={language}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-2 justify-end pt-1">
                                                            <button 
                                                                onClick={() => setEditingSessionId(null)}
                                                                className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg border border-slate-200 dark:border-slate-600 transition-colors"
                                                            >
                                                                {t('cancel')}
                                                            </button>
                                                            <button 
                                                                onClick={handleSaveEdit}
                                                                className="px-4 py-2 text-xs font-bold text-white bg-violet-600 hover:bg-violet-700 rounded-lg shadow-sm transition-colors"
                                                            >
                                                                {t('save')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div key={session.id} className="flex items-center justify-between text-xs p-2.5 rounded bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700">
                                                    <div className="flex items-center gap-2.5">
                                                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: FASTING_PERIOD_COLORS[index % FASTING_PERIOD_COLORS.length] }} />
                                                        <span className="font-medium text-slate-600 dark:text-slate-300">
                                                            {startStr} - {endStr}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <span className="font-bold text-slate-700 dark:text-slate-200">
                                                            {formatDayTotal(duration)}
                                                        </span>
                                                        <div className="flex items-center gap-1.5 border-l border-slate-200 dark:border-slate-700 pl-3">
                                                            <button onClick={() => handleEditClick(session)} className="text-slate-400 hover:text-violet-500 p-0.5">
                                                                <PencilIcon className="w-3.5 h-3.5" />
                                                            </button>
                                                            <button onClick={() => handleDelete(session.id)} className="text-slate-400 hover:text-red-500 p-0.5">
                                                                <TrashIcon className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                <div className="p-4 border-t border-slate-100 dark:border-slate-800">
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-full py-3 bg-slate-800 dark:bg-slate-700 text-white font-black rounded-xl"
                    >
                        {t('close')}
                    </button>
                </div>
            </div>
            <ConfirmationModal
                isOpen={!!sessionToDelete}
                title={t('confirmDeleteFastingSessionTitle') || 'Delete Fasting Session'}
                message={t('confirmDeleteFastingSession') || 'Are you sure you want to delete this session?'}
                onConfirm={confirmDelete}
                onCancel={() => setSessionToDelete(null)}
                confirmText={t('delete') || 'Delete'}
                cancelText={t('cancel') || 'Cancel'}
            />
        </div>
    );
};

export default FastingStatisticsModal;
